<template>
  <el-dialog
    :visible.sync="chatDialogVisible"
    :modal="false"
    :close-on-click-modal="false"
    custom-class="chat-dialog"
    v-dialogDrag
    :append-to-body="true"
    :show-close="false"
    width="600px"
  >
    <div class="chat-header" v-drag>
      <div class="chat-title">
        <img :src="avatarImg" alt="" class="chat-avatar" />
        <span>{{ staffName }}</span>
      </div>
      <i class="el-icon-close" @click="closeChatDialogVisible"></i>
    </div>
    <div class="chat-body">
      <div class="chat-messages" ref="chatMessages" @scroll="handleScroll">
        <!-- 猜你想问区域 v-if="showGuessSection"-->
        <div class="guess-section">
          <div class="guess-header">
            <span class="guess-title">猜你想问</span>
            <span class="guess-refresh" @click="refreshQuestions">
              <i class="el-icon-refresh"></i> 换一换
            </span>
            <i
              class="el-icon-close guess-close"
              style="font-weight: 700; color: #333"
              @click="showGuessSection = false"
            ></i>
          </div>
          <div class="guess-list">
            <div
              class="guess-item"
              v-for="(question, index) in guessQuestions"
              :key="index"
              @click="selectQuestion(question)"
            >
              <span class="question-num">{{ index + 1 }}.</span>
              <span class="question-text">{{ question }}</span>
              <i class="el-icon-arrow-right"></i>
            </div>
          </div>
        </div>

        <!-- 消息列表 -->
        <div
          v-for="(msg, index) in messages"
          :key="'msg-' + index"
          :class="[
            'message-item',
            msg.type === 'user' ? 'user-message' : 'bot-message',
          ]"
        >
          <div class="message-avatar">
            <img v-if="msg.type === 'bot'" :src="avatarImg" alt="" />
            <img v-else :src="userAvatarImg" alt="" />
          </div>
          <div class="message-content-wrapper">
            <!-- 文本消息 -->
            <div
              v-if="!msg.contentType || msg.contentType === 'text'"
              :class="['message-content', { streaming: msg.streaming }]"
            >
              <!-- 流式等待进度（内容为空时显示） -->
              <div v-if="msg.streaming && !msg.content && streamingProgress" class="stream-status-inline">
                <span class="stream-status-dot"></span>
                <span class="stream-status-text">{{ streamingProgress }}</span>
              </div>
              <!-- 流式中：纯文本(含{{attachment}}占位符)，完成后 v-html 渲染图片 -->
              <span v-if="msg.streaming" class="msg-text" v-html="msgHtmlContent(msg)"></span>
              <span v-else class="msg-text" v-html="msgHtmlContent(msg)"></span>
            </div>
            <!-- 图片消息 -->
            <div
              v-else-if="msg.contentType === 'image'"
              class="message-content image-message"
            >
              <el-image
                style="width: 100px; height: 100px"
                :src="msg.content"
                :preview-src-list="[msg.content]"
              >
              </el-image>
            </div>
            <!-- 文件消息 -->
            <div
              v-else-if="msg.contentType === 'file'"
              class="message-content file-message"
            >
              <div class="file-icon">
                <i class="el-icon-document"></i>
              </div>
              <div class="file-info">
                <div class="file-name">{{ msg.fileName }}</div>
                <div class="file-size">{{ msg.fileSize }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- 评价打分区域 - 放在消息列表后面 -->
        <div class="rating-section" v-if="showRatingSection">
          <div class="rating-header">
            <span class="rating-title">请对本次服务进行评价</span>
            <i class="el-icon-close rating-close" @click="closeRating"></i>
          </div>
          <div class="rating-emojis">
            <div
              class="emoji-item"
              v-for="(emoji, index) in ratingEmojis"
              :key="index"
              :class="{ active: selectedRating === index }"
              @click="selectRating(index)"
            >
              <span class="emoji-icon">{{ emoji.icon }}</span>
              <span class="emoji-text">{{ emoji.text }}</span>
            </div>
          </div>
          <div class="rating-tags">
            <span
              class="rating-tag"
              v-for="(tag, index) in ratingTags"
              :key="index"
              :class="{ active: selectedTags.includes(index) }"
              @click="toggleTag(index)"
              >{{ tag }}</span
            >
          </div>
          <div class="rating-input">
            <el-input
              type="textarea"
              v-model="ratingComment"
              placeholder="请说明原因"
              :rows="3"
              resize="none"
            ></el-input>
          </div>
          <div class="rating-submit">
            <el-button type="primary" size="small" @click="submitRating"
              >提交</el-button
            >
          </div>
        </div>
      </div>

      <!-- 结束对话按钮 -->
      <div class="end-chat-section" v-if="!isChatEnded">
        <el-tooltip
          v-if="showtransfer"
          placement="top"
          effect="light"
          :content="isBotResponding ? '机器人正在回复中，请稍后再转人工' : '当前为智能客服，点击可转接人工客服为您服务'"
          popper-class="chat-guide-tooltip"
        >
          <el-button
            class="end-chat-btn"
            size="small"
            :disabled="isBotResponding"
            @click="transferToHuman"
            >转人工</el-button
          >
        </el-tooltip>
        <el-button
          class="end-chat-btn"
          v-if="!showRatingSection"
          size="small"
          @click="endChat"
        >
          <img class="img" src="@/assets/image/结束.svg" alt="" srcset="" />
          结束对话
        </el-button>
      </div>
    </div>
    <div class="chat-footer">
      <div class="toolbar">
        <div class="toolbar-item emoji-trigger">
          <img
            src="@/assets/image/表情.svg"
            class="toolbar-icon"
            alt="表情"
            srcset=""
            @click="toggleEmojiPicker"
          />
          <!-- 表情选择器 -->
          <div class="emoji-picker" v-if="showEmojiPicker" @click.stop>
            <div class="emoji-picker-header">
              <span>表情</span>
              <i class="el-icon-close" @click="showEmojiPicker = false"></i>
            </div>
            <div class="emoji-picker-content">
              <div
                class="emoji-item"
                v-for="(emoji, index) in emojiList"
                :key="index"
                @click="insertEmoji(emoji)"
                :title="emoji.alt"
              >
                <img :src="(baseUrl + 'static/emoji/' + emoji.url)" :alt="emoji.alt" />
              </div>
            </div>
          </div>
        </div>
        <div class="toolbar-item">
          <i
            class="el-icon-picture-outline toolbar-icon"
            :class="{ 'toolbar-icon-disabled': pendingImages.length >= 3 }"
            title="发送图片"
            @click="triggerImageUpload"
          ></i>
          <input
            ref="imageInput"
            type="file"
            accept="image/*"
            multiple
            style="display: none"
            @change="handleImageUpload"
          />
        </div>
        <div class="toolbar-item">
          <el-tooltip placement="top" effect="light" popper-class="chat-guide-tooltip">
            <div slot="content" class="chat-guide-content">
              <p class="chat-guide-title">客服操作指南</p>
              <p>1. 点击 <i class="el-icon-picture-outline"></i> 可发送图片，最多3张</p>
              <p>2. 点击 <img src="@/assets/image/表情.svg" class="chat-guide-icon" /> 可插入表情</p>
              <p>3. 输入问题后按回车或点击「发送」即可咨询客服</p>
              <p>4. 智能客服无法解决时，可点击「转人工」转接人工客服</p>
              <p>5. 服务结束后可点击「结束对话」并进行满意度评价</p>
            </div>
            <img
              src="@/assets/image/常见问题.svg"
              class="toolbar-icon"
              alt="问题咨询"
              srcset=""
            />
          </el-tooltip>
        </div>
      </div>
      <div class="input-area">
        <!-- 待发送图片预览（最多3张）：选图后先暂存，可继续输入文字，点击发送才真正发出 -->
        <div class="pending-image-preview" v-if="pendingImages.length">
          <div class="pending-image-item" v-for="(img, idx) in pendingImages" :key="idx">
            <img :src="img.previewUrl" />
            <i class="el-icon-circle-close remove-btn" @click="removePendingImage(idx)"></i>
          </div>
        </div>
        <el-input
          type="textarea"
          v-model="inputMessage"
          placeholder="很高兴为您服务，请输入您想咨询的问题~"
          :rows="2"
          resize="none"
          :disabled="isBotResponding || isSendingMessage"
          @keyup.enter.native.prevent="sendMessage"
          @click.native="showEmojiPicker = false"
        ></el-input>
        <el-button
          type="primary"
          class="send-btn"
          :loading="isSendingMessage"
          :disabled="isBotResponding || isSendingMessage"
          @click="sendMessage"
        >发送</el-button>
      </div>
    </div>
    <el-image
      ref="previewImage"
      :src="previewUrl"
      :preview-src-list="previewList"
      style="display: none"
    />
  </el-dialog>
</template>

<script>
import { getFingerprintId } from "../../utils/finger";
import {
  sendMessagemsg,
  uploadCsAttachment,
  transferToHuman,
  closeSession,
  getUserServiceList,
  evaluateService,
} from "@/assets/API/customerService";
const WSURl = process.env.VUE_APP_WS_WEBSOCKET;
export default {
  name: "ChatDialog",
  directives: {
    drag: {
      inserted(el) {
        const dialogHeaderEl = el;
        const dragDom = el.closest(".el-dialog");
        if (!dragDom) return;

        dialogHeaderEl.style.cursor = "move";

        // 获取原有属性
        const getStyle = (function () {
          if (window.document.currentStyle) {
            return (dom, attr) => dom.currentStyle[attr];
          } else {
            return (dom, attr) => getComputedStyle(dom, false)[attr];
          }
        })();

        dialogHeaderEl.onmousedown = (e) => {
          // 鼠标按下，计算当前元素距离可视区的距离
          const disX = e.clientX - dialogHeaderEl.offsetLeft;
          const disY = e.clientY - dialogHeaderEl.offsetTop;

          const dragDomWidth = dragDom.offsetWidth;
          const dragDomHeight = dragDom.offsetHeight;

          const screenWidth = document.body.clientWidth;
          const screenHeight = document.body.clientHeight;

          const minDragDomLeft = dragDom.offsetLeft;
          const maxDragDomLeft =
            screenWidth - dragDom.offsetLeft - dragDomWidth;

          const minDragDomTop = dragDom.offsetTop;
          const maxDragDomTop =
            screenHeight - dragDom.offsetTop - dragDomHeight;

          // 获取到的值带px 正则匹配替换
          let styL = getStyle(dragDom, "left");
          let styT = getStyle(dragDom, "top");

          if (styL.includes("%")) {
            styL =
              +document.body.clientWidth * (+styL.replace(/\%/g, "") / 100);
            styT =
              +document.body.clientHeight * (+styT.replace(/\%/g, "") / 100);
          } else {
            styL = +styL.replace(/\px/g, "");
            styT = +styT.replace(/\px/g, "");
          }

          document.onmousemove = function (e) {
            // 通过事件委托，计算移动的距离
            let left = e.clientX - disX;
            let top = e.clientY - disY;

            // 边界处理
            if (-left > minDragDomLeft) {
              left = -minDragDomLeft;
            } else if (left > maxDragDomLeft) {
              left = maxDragDomLeft;
            }

            if (-top > minDragDomTop) {
              top = -minDragDomTop;
            } else if (top > maxDragDomTop) {
              top = maxDragDomTop;
            }

            // 移动当前元素
            dragDom.style.cssText += `;left:${left + styL}px;top:${
              top + styT
            }px;`;
          };

          document.onmouseup = function () {
            document.onmousemove = null;
            document.onmouseup = null;
          };
        };
      },
      unbind(el) {
        // 清理事件监听器
        const dialogHeaderEl = el;
        dialogHeaderEl.onmousedown = null; // 直接置为 null 来移除赋值的事件处理器
      },
    },
  },
  data() {
    return {
      avatarImg: require("../../assets/logo.png"),
      userAvatarImg: require("../../assets/image/person_icon.png"),
      chatDialogVisible: false,
      inputMessage: "",
      // 暂存待发送的图片（选择后先预览，点击发送时才真正随文字一起发出，最多3张）
      pendingImages: [],
      showGuessSection: true,
      showRatingSection: false,
      isChatEnded: false,
      showEmojiPicker: false,
      emojiList: [],
      selectedRating: 4, // 默认选中"超预期"
      selectedTags: [0],
      ratingComment: "",
      ratingEmojis: [
        { icon: "😟", text: "不满意" },
        { icon: "😐", text: "一般" },
        { icon: "🙂", text: "满意" },
        { icon: "😊", text: "很满意" },
        { icon: "🤩", text: "超预期" },
      ],
      ratingTags: ["沟通顺畅", "操作便捷", "回答准确", "试用不错", "回复缓慢"],
      guessQuestions: [
        "软件许可是否通用？",
        "许可文件无法放入到安装目录下该如何处理？",
        "通过设置许可文件按钮设置许可后软件无法使用怎么办？",
      ],
      messages: [],
      // 流式回复状态
      isBotResponding: false,
      isSendingMessage: false,
      currentBotMessageId: null,
      streamingProgress: '',
      // 图片缓存按 messageId 隔离，避免不同轮次机器人回复互相污染
      imageUrlCacheByMessage: {},
      // 打字机缓冲区：{ messageId: '剩余待输出的字符' }
      typewriterBuffers: {},
      typewriterTimer: null,
      // 流式回复超时兜底定时器
      botReplyTimeoutTimer: null,
      // WebSocket 和 API 相关
      ws: null,
      wsConnectPromise: null,
      wsReconnectTimer: null,
      currentSessionId: null,
      currentServiceStaffId: null,
      userId: "",
      fingerprintId: null,
      showtransfer: true,
      staffName: "客服",
      // 上拉加载相关
      hasMoreHistory: true,
      historyPageNum: 1,
      isLoadingHistory: false,
      previewUrl: '',
      previewList: []
    };
  },
  computed: {
    userInfo() {
      return this.$store.state.user.userInfo;
    },
    baseUrl() {
      return process.env.BASE_URL || '/';
    },
  },
  async mounted() {
    this.loadEmojiList();
    // 点击其他地方关闭表情选择器
    document.addEventListener("click", this.closeEmojiPicker);
    document.addEventListener("click", this.handleImagePreview);
    // 获取指纹ID并连接WebSocket
    this.fingerprintId = await getFingerprintId();
    this.userId = this.userInfo && this.userInfo.id ? this.userInfo.id : "";
  },
  beforeDestroy() {
    document.removeEventListener("click", this.closeEmojiPicker);
    document.removeEventListener("click", this.handleImagePreview);
    this.stopTypewriter();
    if (this.botReplyTimeoutTimer) {
      clearTimeout(this.botReplyTimeoutTimer);
      this.botReplyTimeoutTimer = null;
    }
    // 清理 WebSocket 和轮询
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.close();
    }
    if (this.wsReconnectTimer) {
      clearTimeout(this.wsReconnectTimer);
      this.wsReconnectTimer = null;
    }
  },
  methods: {
    handleImagePreview(e) {
      // 检查点击的元素是否是可预览的图片
      if (e.target.classList.contains("previewable-image")) {
        const url = e.target.currentSrc;
        this.previewUrl = url;
        this.previewList = [url];
        this.$nextTick(() => {
          const elImage = this.$refs.previewImage;
          if (elImage) {
            elImage.showViewer = true;  // 或者 elImage.preview()
          }
        });
      }
    },
    // 打开对话框
    async open() {
      this.chatDialogVisible = true;
      this.messages = [];
      
      // 重置UI状态(防止上次关闭时的状态残留)
      this.isChatEnded = false;
      this.showRatingSection = false;
      this.showGuessSection = true;
      this.clearPendingImages();
      
      // 重置流式状态
      this.isBotResponding = false;
      this.currentBotMessageId = null;
      this.streamingProgress = '';
      this.imageUrlCacheByMessage = {};
      this.stopTypewriter();
      this.typewriterBuffers = {};
      if (this.botReplyTimeoutTimer) {
        clearTimeout(this.botReplyTimeoutTimer);
        this.botReplyTimeoutTimer = null;
      }
      
      // 重置历史消息加载状态
      this.hasMoreHistory = true;
      this.historyPageNum = 1;
      this.isLoadingHistory = false;
      
      // 连接WebSocket并加载历史消息
      this.connectWebSocket().catch((error) => {
        console.warn("WebSocket 预连接未完成，将在发送消息时重试:", error);
      });
      await this.loadHistoryMessages();
    },
    // 关闭对话框
    closeChatDialogVisible() {
      this.chatDialogVisible = false;
      this.stopTypewriter();
      if (this.botReplyTimeoutTimer) {
        clearTimeout(this.botReplyTimeoutTimer);
        this.botReplyTimeoutTimer = null;
      }
      if (this.ws) {
        console.log("WebSocket 连接关闭");
        // 清理 WebSocket 事件处理器
        this.ws.onopen = null;
        this.ws.onmessage = null;
        this.ws.onerror = null;
        this.ws.onclose = null;
        // 关闭连接
        this.ws.close();
        this.ws = null; // 置为 null，避免重复使用
      }
      this.wsConnectPromise = null;
      if (this.wsReconnectTimer) {
        clearTimeout(this.wsReconnectTimer);
        this.wsReconnectTimer = null;
      }
    },
    // WebSocket 连接
    connectWebSocket() {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        return Promise.resolve(true);
      }
      if (this.ws && this.ws.readyState === WebSocket.CONNECTING && this.wsConnectPromise) {
        return this.wsConnectPromise;
      }

      const aaa = this.userId ? this.userId : this.fingerprintId;
      if (!aaa) {
        return Promise.reject(new Error("客户身份初始化中"));
      }
      const wsUrl = WSURl + aaa;

      this.wsConnectPromise = new Promise((resolve, reject) => {
        let socket;
        let settled = false;
        const finish = (callback, value) => {
          if (settled) return;
          settled = true;
          clearTimeout(connectTimer);
          this.wsConnectPromise = null;
          callback(value);
        };
        const connectTimer = setTimeout(() => {
          if (socket && socket.readyState === WebSocket.CONNECTING) socket.close();
          finish(reject, new Error("客服连接超时，请稍后重试"));
        }, 15000);

        try {
          socket = new WebSocket(wsUrl);
          this.ws = socket;

          socket.onopen = () => {
            console.log("WebSocket 连接成功", wsUrl);
            finish(resolve, true);
            if (this.currentSessionId) this.registerSession();
          };
          socket.onmessage = (event) => {
            try {
              const data = JSON.parse(event.data);
              console.log("收到 WebSocket 消息:", data);
              this.handleWebSocketMessage(data);
            } catch (e) {
              console.error("解析消息失败:", e);
            }
          };

          socket.onerror = (error) => {
            console.error("WebSocket 错误:", error);
            finish(reject, new Error("客服连接失败"));
          };

          socket.onclose = () => {
            console.log("WebSocket 连接关闭");
            if (this.ws === socket) this.ws = null;
            finish(reject, new Error("客服连接已断开"));
            if (!this.chatDialogVisible || this.wsReconnectTimer) return;
            this.wsReconnectTimer = setTimeout(() => {
              this.wsReconnectTimer = null;
              console.log("尝试重新连接...");
              this.connectWebSocket().catch(() => {});
            }, 5000);
          };
        } catch (e) {
          console.error("WebSocket 连接失败:", e);
          finish(reject, e);
        }
      });
      return this.wsConnectPromise;
    },
    // 注册会话
    registerSession() {
      if (this.ws && this.ws.readyState === WebSocket.OPEN && this.currentSessionId) {
        const message = {
          action: "register",
          refId: this.currentSessionId,
        };
        this.ws.send(JSON.stringify(message));
        console.log("已注册会话:", this.currentSessionId);
        return true;
      }
      return false;
    },
    // 处理 WebSocket 消息
    handleWebSocketMessage(data) {
      console.log("处理消息类型:", data.type);

      switch (data.type) {
        case "connected":
          console.log("连接确认:", data.message);
          break;

        case "newMessage":
          // 收到新消息
          if (data.data) {
            const msg = data.data;
            let type = "bot";
            if (msg.contentType === 1) type = "user";
            else if (msg.contentType === 2) type = "staff"; // 客服回复
            else if (msg.contentType === 3) type = "bot";   // 机器人回复
            else if (msg.contentType === 4) type = "system"; // 系统消息

            if (msg.realName) {
              this.staffName = msg.realName || "客服";
            }
            // 处理附件（图片/文件）
            let attachments = [];
            try {
              attachments = msg.attachments ? (typeof msg.attachments === "string" ? JSON.parse(msg.attachments) : msg.attachments) : [];
            } catch (e) {
              console.warn("解析 attachments 失败:", e);
            }
            if (attachments.length > 0) {
              // 根据消息来源选择图片地址前缀
              // 机器人回复(contentType===3)的图片来自 RAG 系统
              // 客服/用户发送的图片来自主后端
              const imgBaseUrl = msg.contentType === 3
                ? (process.env.VUE_APP_IMG_URL || 'http://192.168.0.184:8080')
                : process.env.VUE_APP_BASE_API;
              let imageMap = {};
              attachments.forEach((img) => {
                imageMap[img.id] = img;
              });
              const content = msg.content.replace(
                /\{\{attachment_\d+\}\}/g,
                (match) => {
                  const imageId = match.replace(/\{|\}/g, ""); // 去掉花括号
                  const image = imageMap[imageId];
                  if (image) {
                    const imageUrl = image.url.startsWith('http') ? image.url : imgBaseUrl + image.url;
                    return `<img src="${imageUrl}" alt="${
                      image.name || "图片"
                    }" class="chat-image previewable-image" style="max-width: 200px; display: block; margin: 5px 0;" />`;
                  }
                  return match; // 没找到就保留原样
                }
              );
              this.messages.push({
                type: type,
                content: content,
                contentType: "text",
              });
            } else {
              this.messages.push({
                type: type,
                content: msg.content,
                contentType: "text",
              });
            }
          }
          break;

        case "systemMessage":
          // 系统消息（如转接通知，可能携带 realName 用于更新当前客服姓名）
          if (data.data) {
            if (data.data.realName) {
              this.staffName = data.data.realName;
              this.showtransfer = false; // 已转人工，隐藏转人工按钮
            }
            this.messages.push({
              type: "bot",
              content: data.data.content,
              contentType: "text",
            });
          }
          break;

        case "messageSent":
          console.log("消息发送成功确认");
          break;

        case "error":
          console.error("服务器错误:", data.message);
          this.$message.error(data.message);
          break;

        // ===== 流式 RAG 回复事件 =====
        case "botReplyStart":
          this.isBotResponding = true;
          this.currentBotMessageId = data.data.messageId;
          this.$set(this.imageUrlCacheByMessage, data.data.messageId, {});
          this.messages.push({
            type: "bot",
            contentType: "text",
            content: "",
            streaming: true,
            id: data.data.messageId,
            messageId: data.data.messageId,
            // 渲染缓存版本，用于减少重复正则替换
            imageMapVersion: 0,
            _renderCacheKey: '',
            _renderCacheHtml: '',
            finalHtmlContent: ''
          });
          // 超时兜底：60秒后若仍在 streaming，强制结束，防止 ws 异常断开导致消息卡住
          if (this.botReplyTimeoutTimer) clearTimeout(this.botReplyTimeoutTimer);
          this.botReplyTimeoutTimer = setTimeout(() => {
            const stuckMsg = this.messages.find(m => m.messageId === data.data.messageId);
            if (stuckMsg && stuckMsg.streaming) {
              console.warn('机器人回复超时，强制结束 streaming 状态');
              stuckMsg.streaming = false;
              this.$set(stuckMsg, 'streaming', false);
              if (!stuckMsg.content) {
                stuckMsg.content = '回复超时，请稍后重试。';
                this.$set(stuckMsg, 'content', stuckMsg.content);
              }
              this.isBotResponding = false;
              this.currentBotMessageId = null;
              this.stopTypewriter();
              this.scrollToBottom();
            }
          }, 60000);
          this.scrollToBottom();
          break;

        case "botReplyProgress":
          this.streamingProgress = data.data.message || '';
          break;

        case "botReplyImages":
          if (data.data && data.data.attachments && data.data.messageId) {
            try {
              const atts = JSON.parse(data.data.attachments);
              this.updateImageMapForMessage(data.data.messageId, atts, false);
            } catch (e) {
              console.warn('botReplyImages attachments 解析失败:', e);
            }
          }
          break;

        case "botReplyChunk":
          this.streamingProgress = '';
          if (data.data && data.data.chunk && data.data.messageId) {
            // 追加到打字机缓冲区，由定时器逐字释放到 msg.content
            if (!this.typewriterBuffers[data.data.messageId]) {
              this.typewriterBuffers[data.data.messageId] = '';
            }
            this.typewriterBuffers[data.data.messageId] += data.data.chunk;
            this.startTypewriter();
          }
          break;

        case "botReplyComplete":
          if (data.data && data.data.messageId) {
            // 先把缓冲区剩余内容全部输出
            this.flushTypewriterBuffer(data.data.messageId);
            const compMsg = this.messages.find(m => m.messageId === data.data.messageId);
            if (compMsg) {
              // 如果打字机没输出任何内容（异常情况），用服务端内容兜底
              if (!compMsg.content && data.data.content) {
                compMsg.content = data.data.content;
                this.$set(compMsg, 'content', compMsg.content);
              }
              // 兜底：如果 botReplyImages 没推送或 imageUrlCache 缺失，用 complete 事件的 attachments 补齐
              if (data.data.attachments) {
                try {
                  const atts = JSON.parse(data.data.attachments);
                  this.updateImageMapForMessage(data.data.messageId, atts, true);
                } catch (e) {
                  console.warn('botReplyComplete attachments 解析失败:', e);
                }
              }
              compMsg.streaming = false;
              this.$set(compMsg, 'streaming', false);
              this.freezeFinalMessageHtml(compMsg);
              this.scrollToBottom();
            }
          }
          this.isBotResponding = false;
          this.currentBotMessageId = null;
          this.streamingProgress = '';
          this.stopTypewriter();
          if (this.botReplyTimeoutTimer) {
            clearTimeout(this.botReplyTimeoutTimer);
            this.botReplyTimeoutTimer = null;
          }
          break;

        case "botReplyError":
          console.error("机器人回复错误:", data.data);
          this.stopTypewriter();
          if (this.botReplyTimeoutTimer) {
            clearTimeout(this.botReplyTimeoutTimer);
            this.botReplyTimeoutTimer = null;
          }
          if (data.data && data.data.messageId) {
            const errMsg = this.messages.find(m => m.messageId === data.data.messageId);
            if (errMsg) {
              if (!errMsg.content) {
                errMsg.content = '抱歉，自动回复出现异常，请重新发送。';
              }
              errMsg.streaming = false;
              this.$set(errMsg, 'streaming', false);
              this.scrollToBottom();
            }
          }
          this.isBotResponding = false;
          this.currentBotMessageId = null;
          this.streamingProgress = '';
          this.$message.error(data.data && data.data.error ? data.data.error : '机器人回复失败');
          break;

        default:
          console.log("未知消息类型:", data.type);
      }
      this.scrollToBottom();
    },
    // 加载表情列表
    async loadEmojiList() {
      try {
        const response = await fetch(process.env.BASE_URL + "static/emoji.json");
        this.emojiList = await response.json();
      } catch (error) {
        console.error("加载表情列表失败:", error);
        this.emojiList = [];
      }
    },
    // 切换表情选择器显示
    toggleEmojiPicker() {
      this.showEmojiPicker = !this.showEmojiPicker;
    },
    // 关闭表情选择器
    closeEmojiPicker(e) {
      if (!e.target.closest(".emoji-trigger")) {
        this.showEmojiPicker = false;
      }
    },
    // 插入表情（发送为图片消息，走真实发送流程）
    insertEmoji(emoji) {
      this.showEmojiPicker = false;
      const url = window.location.origin + this.baseUrl + "static/emoji/" + emoji.url;
      this.sendImageMessage(url);
    },
    // 触发图片上传
    triggerImageUpload() {
      if (this.pendingImages.length >= 3) {
        this.$message.warning("最多只能同时发送3张图片，请先发送或移除当前图片");
        return;
      }
      this.$refs.imageInput.click();
    },
    // 处理图片上传：先上传获取 URL，暂存预览，等用户点击发送时再一起发出
    handleImageUpload(e) {
      const files = Array.from(e.target.files || []);
      e.target.value = ""; // 清空input，允许重复选择同一文件

      if (!files.length) return;

      const remain = 3 - this.pendingImages.length;
      if (remain <= 0) {
        this.$message.warning("最多只能同时发送3张图片");
        return;
      }
      if (files.length > remain) {
        this.$message.warning(`最多只能同时发送3张图片，已为您选取前${remain}张`);
      }
      const toUpload = files.slice(0, remain);

      toUpload.forEach((file) => {
        // 验证文件类型
        if (!file.type.startsWith("image/")) {
          this.$message.error("请选择图片文件");
          return;
        }

        // 验证文件大小（最大2MB）
        if (file.size > 2 * 1024 * 1024) {
          this.$message.error("图片大小不能超过2MB");
          return;
        }

        this.stagePendingImage(file);
      });
    },
    // 上传图片并暂存为待发送状态（不立即发送）
    async stagePendingImage(file) {
      const previewUrl = URL.createObjectURL(file);
      try {
        const res = await uploadCsAttachment(file);
        if (res.code !== 200) {
          URL.revokeObjectURL(previewUrl);
          this.$message.error("图片上传失败: " + res.msg);
          return;
        }
        this.pendingImages.push({ url: res.data.url, previewUrl });
      } catch (error) {
        URL.revokeObjectURL(previewUrl);
        console.error("图片上传失败:", error);
        this.$message.error("图片上传失败: " + error.message);
      }
    },
    // 移除已暂存待发送的某张图片
    removePendingImage(index) {
      const img = this.pendingImages[index];
      if (img && img.previewUrl) {
        URL.revokeObjectURL(img.previewUrl);
      }
      this.pendingImages.splice(index, 1);
    },
    // 清空所有待发送图片
    clearPendingImages() {
      this.pendingImages.forEach((img) => {
        if (img.previewUrl) URL.revokeObjectURL(img.previewUrl);
      });
      this.pendingImages = [];
    },
    // 以图片消息形式发送（表情共用）
    async sendImageMessage(url) {
      if (this.isBotResponding || this.isSendingMessage) {
        this.$message.warning("正在等待机器人回复，请稍候...");
        return;
      }
      const attachments = [{ id: "attachment_0", url, type: "image" }];
      this.isSendingMessage = true;
      try {
        await this.connectWebSocket();
        const response = await sendMessagemsg({
          refId: this.currentSessionId || "",
          userId: this.userId,
          fid: this.fingerprintId,
          content: "{{attachment_0}}",
          attachments: JSON.stringify(attachments),
          contentType: 1,
          device: process.env.VUE_APP_RESOURCE_URL,
          lang: "zh-CN",
        });

        if (response.code === 200) {
          this.currentSessionId = response.data.refId;
          this.registerSession();

          const displayUrl = url.startsWith("http")
            ? url
            : process.env.VUE_APP_BASE_API + url;
          this.messages.push({
            type: "user",
            content: displayUrl,
            contentType: "image",
          });
          this.scrollToBottom();
        } else {
          this.$message.error("发送失败: " + response.msg);
        }
      } catch (error) {
        console.error("发送图片消息失败:", error);
        this.$message.error("发送失败: " + error.message);
      } finally {
        this.isSendingMessage = false;
      }
    },
    // 触发文件上传
    triggerFileUpload() {
      this.$refs.fileInput.click();
    },
    // 处理文件上传
    handleFileUpload(e) {
      const file = e.target.files[0];
      if (!file) return;

      // 验证文件大小 (最大10MB)
      if (file.size > 10 * 1024 * 1024) {
        this.$message.error("文件大小不能超过10MB");
        return;
      }

      // 格式化文件大小
      const formatSize = (size) => {
        if (size < 1024) return size + "B";
        if (size < 1024 * 1024) return (size / 1024).toFixed(2) + "KB";
        return ((size / 1024) * 1024).toFixed(2) + "MB";
      };

      // 添加文件消息
      this.messages.push({
        type: "user",
        content: file.name,
        contentType: "file",
        fileName: file.name,
        fileSize: formatSize(file.size),
        fileType: file.type || "未知类型",
      });

      this.scrollToBottom();

      // 模拟机器人回复
      setTimeout(() => {
        this.messages.push({
          type: "bot",
          content: `已收到您发送的文件「${file.name}」，请问有什么可以帮您的？`,
          contentType: "text",
        });
        this.scrollToBottom();
      }, 500);

      // 清空input
      e.target.value = "";
    },
    // 截图功能
    handleScreenshot() {
      // 检查浏览器是否支持屏幕捕获API
      if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
        this.$message.warning(
          "您的浏览器不支持截图功能，请使用系统截图工具（如QQ截图、微信截图）后粘贴发送"
        );
        return;
      }

      this.$message.info("请选择要截取的屏幕区域");

      navigator.mediaDevices
        .getDisplayMedia({ video: true })
        .then((stream) => {
          const video = document.createElement("video");
          video.srcObject = stream;
          video.play();

          video.onloadedmetadata = () => {
            const canvas = document.createElement("canvas");
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(video, 0, 0);

            // 停止屏幕共享
            stream.getTracks().forEach((track) => track.stop());

            // 转换为图片URL
            const imageUrl = canvas.toDataURL("image/png");

            // 添加截图消息
            this.messages.push({
              type: "user",
              content: imageUrl,
              contentType: "image",
              fileName: "截图_" + new Date().getTime() + ".png",
            });

            this.scrollToBottom();

            // 模拟机器人回复
            setTimeout(() => {
              this.messages.push({
                type: "bot",
                content: "已收到您的截图，请问有什么可以帮您的？",
                contentType: "text",
              });
              this.scrollToBottom();
            }, 500);
          };
        })
        .catch((err) => {
          console.log("截图取消或失败:", err);
        });
    },
    // 加载历史消息
    async loadHistoryMessages() {
      if (this.isLoadingHistory || !this.hasMoreHistory) return;

      this.isLoadingHistory = true;

      try {
        const response = await getUserServiceList({
          cstServiceId: "",
          fid: this.fingerprintId,
          pageNum: this.historyPageNum,
          pageSize: 30,
          userId: this.userId,
        });

        if (response.code === 200) {
          const list = response.data.list || [];
         
          
          // 调试日志: 查看API返回的数据结构
          console.log("=== loadHistoryMessages 调试信息 ===");
          console.log("response.data:", response.data);
          console.log("list.length:", list.length);
          if (list.length > 0) {
            console.log("list[0]:", list[0]);
            console.log("list[0].status:", list[0].status);
            console.log("list[0].refId:", list[0].refId);
          }
          console.log("================================");

          const isFirstLoad = this.historyPageNum == 1;
          if (isFirstLoad && list.length > 0) {
            const latestSession = list[0];
            
            // 判断会话状态: 1-进行中, 2-已关闭, 3-超时关闭
            const isClosed = response.data.status === 2 || response.data.status === 3;
            
            console.log("isClosed:", isClosed, "status:", latestSession.status);
            
            if (isClosed) {
              // 情况2: 对话已结束 - 准备开启新对话
              console.log("对话已结束,清空 currentSessionId");
              this.currentSessionId = null; // 清空会话ID
              this.staffName = "客服"; // 重置为默认客服名称，避免残留上次人工客服姓名
              this.showtransfer = true; // 显示转人工按钮
              this.showGuessSection = true; // 显示猜你想问
            } else {
              // 情况1: 对话未结束 - 继续之前的对话
              console.log("对话未结束,设置 currentSessionId:", latestSession.refId);
              this.currentSessionId = latestSession.refId;
              this.staffName = response.data.realName || "客服";
              // 根据是否有人工客服判断是否显示转人工按钮
              this.showtransfer = !response.data.realName;
              this.showGuessSection = false; // 隐藏猜你想问
              if (this.ws && this.ws.readyState === WebSocket.OPEN) {
                this.registerSession();
              }
            }
          } else if (isFirstLoad) {
            // 情况3: 首次加载但没有任何历史会话(全新用户) - 保持默认客服状态
            this.currentSessionId = null;
            this.staffName = "客服";
            this.showtransfer = true;
          }
          if (list.length > 0) {
            const historyMessages = list.reverse().map((item) => {
              // 处理附件（图片/文件）
              let attachments = [];
              try {
                attachments = item.attachments ? (typeof item.attachments === "string" ? JSON.parse(item.attachments) : item.attachments) : [];
              } catch (e) {
                console.warn("解析 attachments 失败:", e);
              }
              if (attachments.length > 0) {
                // 根据消息来源选择图片地址前缀
                // 机器人回复(contentType===3)的图片来自 RAG 系统
                // 客服/用户发送的图片来自主后端
                const imgBaseUrl = item.contentType === 3
                  ? (process.env.VUE_APP_IMG_URL || 'http://192.168.0.184:8080')
                  : process.env.VUE_APP_BASE_API;
                let imageMap = {};
                attachments.forEach((img) => {
                  imageMap[img.id] = img;
                });
                item.content = item.content.replace(
                  /\{\{attachment_\d+\}\}/g,
                  (match) => {
                    const imageId = match.replace(/\{|\}/g, ""); // 去掉花括号
                    const image = imageMap[imageId];
                    if (image) {
                      const imageUrl = image.url.startsWith('http') ? image.url : imgBaseUrl + image.url;
                      return `<img src="${imageUrl}" alt="${
                        image.name || "图片"
                      }" class="chat-image previewable-image" style="max-width: 200px; display: block; margin: 5px 0;" />`;
                    }
                    return match; // 没找到就保留原样
                  }
                );
              }
              return {
                ...item,
                type: item.contentType != 1 ? "bot" : "user",
                content: item.content || null,
                contentType: "text",
              };
            });

            // 首次加载直接替换，翻页加载再前插，避免弹框二次打开时旧消息干扰新消息可见性
            if (isFirstLoad) {
              this.messages = historyMessages;
            } else {
              this.messages.unshift(...historyMessages);
            }

            this.historyPageNum++;

            // 首次打开对话框时，待历史消息插入 DOM 后再滚动到底部，
            // 避免在 messages 还未插入前就滚动导致页面高度变化后滞留在中间
            if (isFirstLoad) {
              this.$nextTick(() => {
                this.scrollToBottom();
              });
            }

            // 如果返回的消息少于30条，说明没有更多历史消息
            if (list.length < 30) {
              this.hasMoreHistory = false;
            }
          } else {
            this.hasMoreHistory = false;
          }
        } else {
          console.error("获取历史消息失败:", response.msg);
          this.hasMoreHistory = false;
        }
      } catch (error) {
        console.error("获取历史消息失败:", error);
        this.hasMoreHistory = false;
      } finally {
        this.isLoadingHistory = false;
      }
    },
    // 处理滚动事件，用于上拉加载
    handleScroll() {
      const chatMessages = this.$refs.chatMessages;
      if (chatMessages.scrollTop === 0) {
        this.loadHistoryMessages();
      }
    },
    refreshQuestions() {
      // 模拟换一换功能
      const allQuestions = [
        "软件许可是否通用？",
        "许可文件无法放入到安装目录下该如何处理？",
        "通过设置许可文件按钮设置许可后软件无法使用怎么办？",
        "如何获取软件安装包？",
        "如何申请试用许可？",
        "软件支持哪些操作系统？",
        "如何联系技术支持？",
        "许可证过期了怎么办？",
        "如何升级软件版本？",
      ];
      // 随机选择3个问题
      const shuffled = allQuestions.sort(() => 0.5 - Math.random());
      this.guessQuestions = shuffled.slice(0, 3);
    },
    selectQuestion(question) {
      this.inputMessage = question;
      this.sendMessage();
    },
    transferToHuman() {
      if (this.isBotResponding) {
        this.$message.warning('机器人正在回复中，请稍后再转人工');
        return;
      }
      // 使用真实的 API 转人工
      this.transferToHumanAPI();
    },
    endChat() {
      // 如果没有会话ID,说明还没开始对话,直接关闭对话框
      if (!this.currentSessionId) {
        this.chatDialogVisible = false;
        return;
      }
      
      // 显示评价区域，隐藏结束对话按钮
      this.showRatingSection = true;
      this.isChatEnded = true;
      this.showGuessSection = false;
      this.scrollToBottom();
      
      closeSession({
        refId: this.currentSessionId,
        status: 2, // 2-已关闭 3-超时关闭
      })
        .then((response) => {
          if (response.code === 200) {
            console.log("会话已结束");
          } else {
            console.error("结束会话失败:", response.msg);
          }
        })
        .catch((error) => {
          console.error("结束会话失败:", error);
        });
    },
    closeRating() {
      // 关闭评价区域
      this.showRatingSection = false;
      this.isChatEnded = false;
      this.resetRating();
    },
    selectRating(index) {
      this.selectedRating = index;
    },
    toggleTag(i) {
      const idx = this.selectedTags.indexOf(i);
      if (idx > -1) {
        this.selectedTags.splice(idx, 1);
      } else {
        this.selectedTags.push(i);
      }
    },
    resetRating() {
      this.selectedRating = 4;
      this.selectedTags = [0];
      this.ratingComment = "";
    },
    submitRating() {
      evaluateService({
        refId: this.currentSessionId,
        serviceEvaluation: this.selectedRating + 1, // 评分接口通常从1开始
        evaluationType: this.selectedTags.join(","), // 选中的标签
        serviceRemark: this.ratingComment,
      })
        .then((response) => {
          if (response.code === 200) {
            console.log("评价提交成功");
            // 显示感谢消息
            this.messages.push({
              type: "bot",
              content: "感谢您的评价！祝您生活愉快！",
            });

            // 关闭评价区域
            this.showRatingSection = false;
            this.isChatEnded = true;
            this.resetRating();
            this.scrollToBottom();

            // 可选：延迟关闭对话框
            setTimeout(() => {
              this.chatDialogVisible = false;
              this.showGuessSection = true;
            }, 1000);
          } else {
            console.error("评价提交失败:", response.msg);
          }
        })
        .catch((error) => {
          console.error("评价提交失败:", error);
        });
    },
    // ========== 打字机效果 ==========
    // 启动打字机定时器（如果尚未启动）
    startTypewriter() {
      if (this.typewriterTimer) return;
      this.typewriterTimer = setInterval(() => {
        this.typewriterTick();
      }, 30); // 每30ms从缓冲区取一批字符输出
    },
    // 打字机定时器回调：从所有活跃缓冲区中取字符输出
    typewriterTick() {
      let hasContent = false;
      const charsPerTick = 2; // 每次输出2个字符，模拟流畅打字效果

      Object.keys(this.typewriterBuffers).forEach(msgId => {
        const buf = this.typewriterBuffers[msgId];
        if (!buf || buf.length === 0) return;

        const chunkMsg = this.messages.find(m => m.messageId === msgId);
        if (!chunkMsg) return;

        hasContent = true;
        // 从缓冲区头部取出字符
        const take = buf.substring(0, charsPerTick);
        this.typewriterBuffers[msgId] = buf.substring(charsPerTick);
        chunkMsg.content += take;
        this.$set(chunkMsg, 'content', chunkMsg.content);
      });

      // 所有缓冲区都空了就停掉定时器
      if (!hasContent) {
        this.stopTypewriter();
      }
      this.scrollToBottom();
    },
    // 停止打字机定时器
    stopTypewriter() {
      if (this.typewriterTimer) {
        clearInterval(this.typewriterTimer);
        this.typewriterTimer = null;
      }
    },
    // 立即刷新指定消息的剩余缓冲区
    flushTypewriterBuffer(msgId) {
      const buf = this.typewriterBuffers[msgId];
      if (!buf || buf.length === 0) return;

      const chunkMsg = this.messages.find(m => m.messageId === msgId);
      if (chunkMsg) {
        chunkMsg.content += buf;
        this.$set(chunkMsg, 'content', chunkMsg.content);
      }
      delete this.typewriterBuffers[msgId];
    },
    // ========== 打字机效果结束 ==========

    scrollToBottom() {
      this.$nextTick(() => {
        const chatMessages = this.$refs.chatMessages;
        if (chatMessages) {
          chatMessages.scrollTop = chatMessages.scrollHeight + 20;
        }
      });
    },
    updateImageMapForMessage(messageId, attachments, onlyIfMissing = false) {
      if (!messageId || !Array.isArray(attachments) || !attachments.length) return;

      if (!this.imageUrlCacheByMessage[messageId]) {
        this.$set(this.imageUrlCacheByMessage, messageId, {});
      }

      const msgMap = this.imageUrlCacheByMessage[messageId];
      const ragBase = process.env.VUE_APP_IMG_URL || 'http://192.168.0.184:8080';
      let changed = false;

      attachments.forEach((att) => {
        if (!att || !att.id || !att.url) return;
        if (onlyIfMissing && msgMap[att.id]) return;

        const fullUrl = att.url.startsWith('http') ? att.url : ragBase + att.url;
        if (msgMap[att.id] !== fullUrl) {
          this.$set(msgMap, att.id, fullUrl);
          changed = true;
        }
      });

      if (changed) {
        const targetMsg = this.messages.find((m) => m.messageId === messageId);
        if (targetMsg) {
          const nextVersion = (targetMsg.imageMapVersion || 0) + 1;
          this.$set(targetMsg, 'imageMapVersion', nextVersion);
        }
      }
    },
    renderMessageHtmlWithMap(content, msgMap) {
      if (!content) return '';

      const buildImgTag = (url, altText = "图片") =>
        `<img src="${url}" alt="${altText}" class="chat-image previewable-image" style="max-width: 200px; display: block; margin: 5px 0;" />`;

      const resolveUrlByToken = (token) => {
        if (!token) return "";
        const normalized = String(token).replace(/^\{\{|\}\}$/g, "");
        return (msgMap && msgMap[normalized]) || "";
      };

      let rendered = content;

      // 1) Markdown 图片语法：![alt](token)
      rendered = rendered.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (match, alt, token) => {
        const url = resolveUrlByToken(token);
        return url ? buildImgTag(url, alt || "图片") : match;
      });

      // 2) 标准附件占位符：{{attachment_N}}
      rendered = rendered.replace(/\{\{([^}]+)\}\}/g, (match, token) => {
        const url = resolveUrlByToken(token);
        return url ? buildImgTag(url) : match;
      });

      // 3) 兼容原始 IMAGE 占位符：[IMAGE_xxx_xxx]
      rendered = rendered.replace(/\[(IMAGE_\d+_\d+)\]/g, (match, token) => {
        const url = resolveUrlByToken(token);
        return url ? buildImgTag(url) : match;
      });

      // 4) 兼容纯 token：IMAGE_xxx_xxx
      rendered = rendered.replace(/\b(IMAGE_\d+_\d+)\b/g, (match, token) => {
        const url = resolveUrlByToken(token);
        return url ? buildImgTag(url) : match;
      });

      return rendered;
    },
    freezeFinalMessageHtml(msg) {
      if (!msg || !msg.messageId) return;
      const msgMap = this.imageUrlCacheByMessage[msg.messageId] || {};
      const finalHtml = this.renderMessageHtmlWithMap(msg.content || '', msgMap);
      this.$set(msg, 'finalHtmlContent', finalHtml);
    },
    // 将消息内容中的 {{attachment_N}} 替换为图片标签（历史/完成时使用）
    msgHtmlContent(msg) {
      if (!msg || !msg.content) return '';

      // 非流式消息优先使用已固化 HTML，避免后续轮次映射更新影响历史回答
      if (!msg.streaming && msg.finalHtmlContent) {
        return msg.finalHtmlContent;
      }

      // 快速路径：无图片标记时直接返回，降低流式过程中重复正则替换开销
      const hasImageTokens = msg.content.includes('{{attachment_')
        || msg.content.includes('IMAGE_')
        || msg.content.includes('![');
      if (!hasImageTokens) {
        return msg.content;
      }

      const cacheKey = `${msg.content}__${msg.imageMapVersion || 0}`;
      if (msg._renderCacheKey === cacheKey && msg._renderCacheHtml !== undefined) {
        return msg._renderCacheHtml;
      }

      const msgMap = this.imageUrlCacheByMessage[msg.messageId] || {};
      const rendered = this.renderMessageHtmlWithMap(msg.content, msgMap);

      this.$set(msg, '_renderCacheKey', cacheKey);
      this.$set(msg, '_renderCacheHtml', rendered);
      return rendered;
    },
    sendMessage() {
      const text = this.inputMessage.replace(/\n/g, "").trim();
      const images = this.pendingImages;
      if (!text && !images.length) {
        this.$message.warning("请输入您想咨询的问题");
        return;
      }
      if (this.isBotResponding || this.isSendingMessage) {
        this.$message.warning('正在等待机器人回复，请稍候...');
        return;
      }
      this.isSendingMessage = true;

      // 添加用户消息（若带图片，则将图片以 <img> 内联展示在文字后面）
      const imageTags = images
        .map((image) => `<img src="${image.url.startsWith("http") ? image.url : process.env.VUE_APP_BASE_API + image.url}" class="chat-image previewable-image" style="max-width: 200px; display: block; margin: 5px 0;" />`)
        .join("");
      const displayContent = text ? `${this.escapeHtml(text)}${imageTags}` : imageTags;
      this.messages.push({
        type: "user",
        content: displayContent,
        contentType: "text",
      });

      this.inputMessage = "";
      this.clearPendingImages();

      // 隐藏猜你想问和表情选择器
      this.showGuessSection = false;
      this.showEmojiPicker = false;

      // 滚动到底部
      this.scrollToBottom();

      // 使用真实的 API 发送消息
      this.sendMessageToAPI(text, images);
    },
    // 转义 HTML 特殊字符，避免用户输入被当作 HTML/脚本注入（消息以 v-html 渲染）
    escapeHtml(str) {
      return String(str || "").replace(/[&<>"']/g, (c) => ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      }[c]));
    },
    // 使用真实的 API 发送消息（images 为可选的已暂存图片数组 [{url}]）
    async sendMessageToAPI(content, images) {
      console.log("=== sendMessageToAPI 调试信息 ===");
      console.log("发送消息前 currentSessionId:", this.currentSessionId);

      let sendContent = content;
      let attachments;
      if (images && images.length) {
        const placeholders = images.map((_, i) => `{{attachment_${i}}}`).join(" ");
        sendContent = content ? `${content} ${placeholders}` : placeholders;
        attachments = JSON.stringify(images.map((image, i) => ({ id: `attachment_${i}`, url: image.url, type: "image" })));
      }

      try {
        // 线上 WSS 握手可能较慢。先确保连接可用再提交消息，避免新会话的
        // 自动回复早于 register 到达，造成首条回复被丢弃。
        await this.connectWebSocket();
        const response = await sendMessagemsg({
          refId: this.currentSessionId || "", // 如果是新对话,refId为空字符串
          userId: this.userId,
          fid: this.fingerprintId,
          content: sendContent,
          attachments,
          contentType: 1, // 客户提问
          device: process.env.VUE_APP_RESOURCE_URL,
          lang: "zh-CN",
        });

        if (response.code === 200) {
          // 更新会话ID(新对话会返回新的refId)
          console.log("消息发送成功,返回的 refId:", response.data.refId);
          this.currentSessionId = response.data.refId;
          console.log("更新后的 currentSessionId:", this.currentSessionId);
          console.log("================================");
          this.registerSession();
        } else {
          this.$message.error("发送失败: " + response.msg);
        }
      } catch (error) {
        console.error("发送消息失败:", error);
        this.$message.error("发送失败: " + error.message);
      } finally {
        this.isSendingMessage = false;
      }
    },
    // 使用真实的 API 转人工
    async transferToHumanAPI() {
      // 如果没有会话ID,说明是新对话,需要先发送消息创建会话
      if (!this.currentSessionId) {
        this.$message.warning("请先发送消息后再转人工");
        return;
      }
      
      try {
        const response = await transferToHuman({
          refId: this.currentSessionId,
          userId: this.userId,
          fid: this.fingerprintId,
          device: process.env.VUE_APP_RESOURCE_URL,
          lang: "zh-CN",
        });

        if (response.code === 200) {
          console.log("转人工成功");
          this.showtransfer = false;
          this.staffName = response.data.realName || "客服";
        } else {
          this.$message.error("转人工失败: " + response.msg);
        }
      } catch (error) {
        console.error("转人工失败:", error);
        this.$message.error("转人工失败: " + error.message);
      }
    },
  },
};
</script>

<style scoped lang="scss">
// 智能客服对话弹窗样式
::v-deep .chat-dialog {
  position: absolute !important;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  right: 60px;
  pointer-events: all;

  .el-dialog__header {
    display: none;
  }

  .el-dialog__body {
    padding: 0;
  }
}

.chat-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background-color: #f7f8fc;
  border-bottom: 1px solid #e5e5e5;
  cursor: move;
  user-select: none;

  .chat-title {
    display: flex;
    align-items: center;

    .chat-avatar {
      width: 36px;
      height: 36px;
      border-radius: 8px;
      margin-right: 10px;
      object-fit: cover;
    }

    span {
      font-size: 16px;
      font-weight: 500;
      color: #333;
    }
  }

  .el-icon-close {
    font-size: 18px;
    cursor: pointer;
    color: #999;
    padding: 4px;

    &:hover {
      color: #666;
    }
  }
}

.chat-body {
  height: 380px;
  background: #f7f8fc;
  display: flex;
  flex-direction: column;
  position: relative;

  .chat-messages {
    flex: 1;
    overflow-y: auto;
    padding: 16px;
    padding-bottom: 48px;

    // 猜你想问区域
    .guess-section {
      background: #fff;
      border-radius: 8px;
      margin-bottom: 16px;
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
      width: 60%;

      .guess-header {
        display: flex;
        align-items: center;
        background: linear-gradient(
          90deg,
          rgba(255, 255, 255, 1) 0%,
          rgba(237, 241, 255, 1) 100%
        );
        padding: 12px;

        .guess-title {
          font-size: 14px;
          font-weight: 600;
          color: #333;
        }

        .guess-refresh {
          margin-left: 12px;
          font-size: 12px;
          color: #2b85e5;
          cursor: pointer;
          display: flex;
          align-items: center;

          i {
            margin-right: 4px;
          }

          &:hover {
            color: #1976d2;
          }
        }

        .guess-close {
          margin-left: auto;
          font-size: 14px;
          color: #999;
          cursor: pointer;

          &:hover {
            color: #666;
          }
        }
      }

      .guess-list {
        padding: 0 10px;
        .guess-item {
          display: flex;
          align-items: center;
          padding: 10px 0;
          border-bottom: 1px solid #f0f0f0;
          cursor: pointer;
          transition: background 0.2s;

          &:last-child {
            border-bottom: none;
          }

          &:hover {
            background: #f9f9f9;
          }

          .question-num {
            color: #333;
            margin-right: 5px;
            font-size: 13px;
          }

          .question-text {
            flex: 1;
            font-size: 13px;
            color: #333;
          }

          .el-icon-arrow-right {
            color: #ccc;
            font-size: 12px;
          }
        }
      }
    }

    // 评价打分区域
    .rating-section {
      background: #fff;
      border-radius: 8px;
      padding: 16px;
      margin-bottom: 16px;
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);

      .rating-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 16px;

        .rating-title {
          font-size: 14px;
          font-weight: 500;
          color: #333;
        }

        .rating-close {
          font-size: 14px;
          color: #999;
          cursor: pointer;

          &:hover {
            color: #666;
          }
        }
      }

      .rating-emojis {
        display: flex;
        justify-content: space-between;
        margin-bottom: 16px;

        .emoji-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          cursor: pointer;
          padding: 8px 12px;
          border-radius: 8px;
          transition: all 0.2s;

          .emoji-icon {
            font-size: 32px;
            margin-bottom: 6px;
            filter: grayscale(100%);
            opacity: 0.6;
            transition: all 0.2s;
          }

          .emoji-text {
            font-size: 12px;
            color: #999;
          }

          &:hover {
            background: #f5f5f5;

            .emoji-icon {
              filter: grayscale(0%);
              opacity: 1;
            }
          }

          &.active {
            .emoji-icon {
              filter: grayscale(0%);
              opacity: 1;
              transform: scale(1.1);
            }

            .emoji-text {
              color: #2b85e5;
              font-weight: 500;
            }
          }
        }
      }

      .rating-tags {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin-bottom: 16px;

        .rating-tag {
          padding: 6px 14px;
          border-radius: 16px;
          border: 1px solid #e0e0e0;
          background: #fff;
          color: #666;
          font-size: 12px;
          cursor: pointer;
          transition: all 0.2s;

          &:hover {
            border-color: #2b85e5;
            color: #2b85e5;
          }

          &.active {
            background: #2b85e5;
            border-color: #2b85e5;
            color: #fff;
          }
        }
      }

      .rating-input {
        margin-bottom: 16px;

        ::v-deep .el-textarea__inner {
          border: 1px solid #e0e0e0;
          border-radius: 8px;
          font-size: 14px;

          &:focus {
            border-color: #2b85e5;
          }

          &::placeholder {
            color: #bbb;
          }
        }
      }

      .rating-submit {
        display: flex;
        justify-content: flex-end;

        .el-button {
          padding: 8px 24px;
          border-radius: 4px;
        }
      }
    }

    // 消息样式
    .message-item {
      display: flex;
      margin-bottom: 16px;
      align-items: flex-start;

      .message-avatar {
        display: flex;
        align-items: center;
        justify-content: center;
        img {
          width: 30px;
          height: 30px;
        }
      }

      .message-content-wrapper {
        display: flex;
        align-items: center;
        gap: 10px;
      }

      .message-content {
        max-width: 260px;
        min-width: 0; // 覆盖 flex 子元素的 min-width: auto，确保文本能正常换行
        padding: 10px 14px;
        border-radius: 8px;
        font-size: 14px;
        line-height: 1.5;
        word-break: break-word;
        overflow-wrap: break-word;

        &.streaming {
            min-height: 1.5em;
          }

          .msg-text {
            white-space: pre-wrap;
            word-break: break-word;
          }

        // 图片消息样式
        &.image-message {
          padding: 4px;
          background: transparent;

          img {
            max-width: 200px;
            max-height: 150px;
            border-radius: 8px;
            cursor: pointer;
            transition: transform 0.2s;

            &:hover {
              transform: scale(1.02);
            }
          }
        }

        // 文件消息样式
        &.file-message {
          display: flex;
          align-items: center;
          background: #fff;
          border: 1px solid #e0e0e0;
          padding: 12px;
          min-width: 180px;

          .file-icon {
            width: 40px;
            height: 40px;
            background: #f0f5ff;
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-right: 10px;

            i {
              font-size: 20px;
              color: #2b85e5;
            }
          }

          .file-info {
            flex: 1;
            overflow: hidden;

            .file-name {
              font-size: 14px;
              color: #333;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              margin-bottom: 4px;
            }

            .file-size {
              font-size: 12px;
              color: #999;
            }
          }
        }
      }

      .transfer-btn-wrapper {
        .inline-transfer-btn {
          border-radius: 16px;
          font-size: 12px;
          padding: 6px 14px;
        }
      }

      &.bot-message {
        .message-avatar {
          margin-right: 10px;
        }

        .message-content {
          background: #dae5ff;
          color: #333;
          //   border-top-left-radius: 0;
        }
      }

      &.user-message {
        flex-direction: row-reverse;

        .message-avatar {
          margin-left: 10px;
        }

        .message-content-wrapper {
          flex-direction: row-reverse;
        }

        .message-content {
          background: #2b85e5;
          color: #fff;
          //   border-top-right-radius: 0;
        }
      }
    }
  }

  // 结束对话区域：浮动在消息区上方的两个悬浮按钮，而非整条横幅
  .end-chat-section {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 8px;
    display: flex;
    justify-content: center;
    gap: 8px;
    padding: 0;
    background: transparent;
    pointer-events: none;

    .end-chat-btn {
      pointer-events: auto;
      border-radius: 16px;
      background: #dae5ff;
      color: #333;
      font-size: 13px;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);

      img {
        margin-right: 4px;
        width: 13px;
        height: 13px;
      }
    }
  }
}

.chat-footer {
  background: #fff;
  border-top: 1px solid #e5e5e5;
  min-height: 150px;
  padding: 10px 16px;
  position: relative;

  .toolbar {
    display: flex;
    padding: 5px 8px;
    gap: 16px;

    .toolbar-item {
      position: relative;
    }

    .toolbar-icon {
      font-size: 18px;
      color: #000;
      cursor: pointer;
      width: 18px;
      height: 18px;

      &:hover {
        color: #2b85e5;
      }

      &.toolbar-icon-disabled {
        color: #ccc;
        cursor: not-allowed;

        &:hover {
          color: #ccc;
        }
      }
    }

    // 表情选择器样式
    .emoji-trigger {
      position: relative;

      .emoji-picker {
        position: absolute;
        bottom: 30px;
        left: 0;
        width: 320px;
        background: #fff;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        z-index: 100;

        .emoji-picker-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 12px;
          border-bottom: 1px solid #f0f0f0;

          span {
            font-size: 14px;
            font-weight: 500;
            color: #333;
          }

          i {
            font-size: 14px;
            color: #999;
            cursor: pointer;

            &:hover {
              color: #666;
            }
          }
        }

        .emoji-picker-content {
          display: grid;
          grid-template-columns: repeat(8, 1fr);
          padding: 8px;
          max-height: 200px;
          overflow-y: auto;

          .emoji-item {
            width: 32px;
            height: 32px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            border-radius: 4px;
            transition: background 0.2s;

            &:hover {
              background: #f0f0f0;
            }

            img {
              width: 24px;
              height: 24px;
            }
          }
        }
      }
    }
  }

  @keyframes botProgressBlink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.3; }
  }
}

/* 流式消息内进度状态（在 .chat-footer 外层） */
.stream-status-inline {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  .stream-status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #409eff;
    animation: botProgressBlink 1s infinite;
  }
  .stream-status-text {
    font-size: 12px;
    color: #409eff;
  }
}
/* 流式消息末尾光标 — 使用 CSS ::after 在父级 */
.message-content.streaming::after {
  content: '|';
  display: inline;
  animation: blink 1s infinite;
  color: #409eff;
  font-weight: bold;
}
@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}

.input-area {
    padding: 8px;
  padding-bottom: 56px;
  position: relative;

    .pending-image-preview {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin-bottom: 8px;

      .pending-image-item {
        position: relative;
        display: inline-block;

        img {
          width: 56px;
          height: 56px;
          object-fit: cover;
          border-radius: 6px;
          border: 2px solid #2b85e5;
          display: block;
        }

        .remove-btn {
          position: absolute;
          top: -8px;
          right: -8px;
          font-size: 16px;
          color: #f56c6c;
          cursor: pointer;
          background: #fff;
          border-radius: 50%;
          line-height: 1;
        }
      }
    }

    ::v-deep .el-textarea {
      flex: 1;
      margin-right: 12px;

      .el-textarea__inner {
        border: none;
        box-shadow: none;
        padding: 0;
        font-size: 13px;
        color: #333;

        &::placeholder {
          color: #bbb;
        }
      }
    }

    .send-btn {
      border-radius: 4px;
      padding: 8px 20px;
      font-size: 14px;
      position: absolute;
      bottom: 16px;
      right: 16px;
    }
  }
</style>

<style lang="scss">
// 客服操作指南提示，非 scoped：el-tooltip 内容会被挂载到 body 上
.chat-guide-tooltip.el-tooltip__popper {
  max-width: 280px;
  line-height: 1.6;

  .chat-guide-content {
    p {
      margin: 4px 0;
      font-size: 12px;
    }

    .chat-guide-title {
      font-size: 13px;
      font-weight: 700;
      margin-bottom: 6px;
    }

    .chat-guide-icon {
      width: 14px;
      height: 14px;
      vertical-align: middle;
    }

    i {
      color: #2b85e5;
    }
  }
}
</style>

